import React from 'react';
/**
 * `ProductPage` wraps the shared `ProductManager` for the dealer journey.
 *
 * It builds a `ProductFilter` from current journey vehicle details and passes
 * quote-related state (expired + acknowledgement) through to `ProductManager`.
 */

import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import ProductManager from '../common/ProductPage/ProductManager';
import { getFormattedDate } from '../common/HelperMethods';
import { ProductChannel } from '../../consts/Common';
import { ProductFilter } from '../common/CommonTypes';
import { useJourney } from './Layout';
import { useAppSelector } from '../../app-state/Hooks';

const ProductPage = (): JSX.Element => {
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.user);
  const cms = useAppSelector((s) => s.content.productPage);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  const {
    journey,
    onUpdateProducts,
    onUpdateProductDocuments,
    onUpdateManufacturingCoverageMonths,
    setIsExpiredMessageAcknowledged
  } = useJourney();

  const {
    products,
    vehicle,
    foundExistingQuotes,
    quoteProducts,
    hasQuoteExpired,
    isExpiredMessageAcknowledged
  } = journey;

  const setIsMessageAcknowledged = () => {
    if (setIsExpiredMessageAcknowledged) {
      setIsExpiredMessageAcknowledged(true);
    }
  };

  const productFilter: ProductFilter = {
    dealerCode: user.dealerCode,
    firstRegistrationDate: vehicle.vehicleDetails.registrationDate,
    vehiclePurchaseDate: getFormattedDate(vehicle.vehicleDeliveryDate),
    vehicleValue: vehicle.vehiclePurchasePrice,
    currentMileage: vehicle.currentMileage ?? 0,
    modelTrimId: vehicle.vehicleDetails.modelTrimId,
    makeId: vehicle.vehicleDetails.makeId,
    modelGroupId: vehicle.vehicleDetails.modelId,
    fuelTypeId: vehicle.vehicleDetails.fuelTypeCode,
    engineSize: parseInt(vehicle.vehicleDetails.engineSize),
    enginePowerBHP: vehicle.vehicleDetails.enginePowerBHP ?? 0,
    enginePowerKWH: vehicle.vehicleDetails.enginePowerKWH ?? 0,
    driveTypeId: vehicle.vehicleDetails.driveTypeId?.toString() ?? '',
    vrn: vehicle.vehicleDetails.vrn,
    channel: ProductChannel.DealerJourney,
    gapDate: getFormattedDate(vehicle.vehicleGapIntroductionDate) ?? '',
    countryCode
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.products')}</title>
      </Helmet>

      <ProductManager
        products={products}
        productFilter={productFilter}
        foundExistingQuotes={foundExistingQuotes}
        cms={cms}
        onUpdateProducts={onUpdateProducts}
        onUpdateProductDocuments={onUpdateProductDocuments}
        onUpdateManufacturingCoverageMonths={
          onUpdateManufacturingCoverageMonths
        }
        token={user.token}
        quoteProducts={quoteProducts}
        hasQuoteExpired={hasQuoteExpired}
        isExpiredMessageAcknowledged={isExpiredMessageAcknowledged}
        setIsExpiredMessageAcknowledged={setIsMessageAcknowledged}
        showKeyBenefits={true}
        showQuotedPrice={true}
        showKeyBenefitsInProductCard={false}
      />
    </>
  );
};

export default ProductPage;
